import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@components/common/button';
import styles from './styles.module.scss';
import TransactionTable from '@components/transactions_table';
import { useNavigate } from 'react-router-dom';
import { routes } from '@const/routes';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiOrderService, apiTransactionsBusinessService, apiTransactionsService } from '@api';
import { ENewNotificationType } from '@enum';
import { useLocales } from '@hooks/helpers/useLocales';
import Tabs from '@components/tabs';
import { ETransactionType, EUserType } from '@xeppt/xeppt-sdk/types';
import { useUserContext } from '@hooks/context/useUserContext';

// Poll the active dashboard list on this cadence (and on tab focus) so a
// request a contact approves on another device reconciles from PENDING to
// APPROVED without the user manually refreshing. Only the active list polls.
const LIST_REFETCH_INTERVAL = 20000;

const allTabs = [
    {
        label: 'Sales',
        value: 'sales'
    },
    {
        label: 'Transactions',
        value: 'transactions'
    },
    {
        label: 'External requests',
        value: 'requests'
    }
];

const TransactionsSection = () => {
    // const { t } = useTranslation('sections', {
    //     keyPrefix: 'dashboard.transactions'
    // });
    const [activeTab, setActiveTab] = useState('transactions');
    const navigate = useNavigate();
    const { submitLocale } = useLocales();
    const { user } = useUserContext();

    useEffect(() => {
        // Wait for user context to hydrate. Before that, `user` is undefined and
        // `undefined !== EUserType.INDIVIDUAL` is true, which would flip an
        // individual account's tab to 'sales' before we actually know their type.
        if (!user) return;
        if (user.type !== EUserType.INDIVIDUAL) {
            setActiveTab('sales');
        } else {
            setActiveTab('transactions');
        }
    }, [user]);

    const {
        data: sales,
        isLoading: isSalesLoading,
        handleRequest: refetchSales
    } = useApiQuery({
        method: () => apiOrderService.findBusinessSales({ page: 1, page_size: 6, order: 'DESC' }),
        notificationType: ENewNotificationType.TRANSACTION,
        // !!user guards against firing while user context is still unresolved
        // (see the mount effect above for why `user?.type !== INDIVIDUAL` alone
        // is true for an undefined user).
        condition: activeTab === 'sales' && !!user && user.type !== EUserType.INDIVIDUAL,
        deps: [activeTab, user],
        refetchOnFocus: true,
        refetchInterval: LIST_REFETCH_INTERVAL
    });

    // Individual (consumer) wallets have no order/external-request partition —
    // this tab is their whole transaction history, so they keep the generic,
    // cursor-based consumer endpoint unchanged.
    const {
        data: transactions,
        isLoading: isTransactionsLoading,
        handleRequest: refetchTransactions
    } = useApiQuery({
        method: () => apiTransactionsService.findTransactions({}, { cursor: new Date(), limit: 6 }),
        notificationType: ENewNotificationType.TRANSACTION,
        condition: activeTab === 'transactions' && user?.type === EUserType.INDIVIDUAL,
        deps: [activeTab, user],
        refetchOnFocus: true,
        refetchInterval: LIST_REFETCH_INTERVAL
    });

    // Business (merchant) wallets: the corrected partition — everything EXCEPT
    // order-associated (Sales) and external-request rows. New endpoint, page-based
    // (not cursor-based) like findBusinessSales above.
    const {
        data: businessTransactions,
        isLoading: isBusinessTransactionsLoading,
        handleRequest: refetchBusinessTransactions
    } = useApiQuery({
        method: () => apiTransactionsBusinessService.listTransactions({ page: 1, page_size: 6 }),
        notificationType: ENewNotificationType.TRANSACTION,
        // !!user guards against firing while user context is still unresolved —
        // otherwise an individual account could hit this business-only endpoint
        // (403) for one render before hydration completes.
        condition: activeTab === 'transactions' && !!user && user.type !== EUserType.INDIVIDUAL,
        deps: [activeTab, user],
        refetchOnFocus: true,
        refetchInterval: LIST_REFETCH_INTERVAL
    });

    const {
        data: intents,
        isLoading: isIntentsLoading,
        handleRequest: refetchIntents
    } = useApiQuery({
        method: () =>
            apiTransactionsService.getPaymentIntents({
                limit: 6,
                cursor: new Date(),
                type: [ETransactionType.DEBIT]
            }),
        notificationType: ENewNotificationType.TRANSACTION,
        condition: activeTab === 'requests',
        deps: [activeTab],
        refetchOnFocus: true,
        refetchInterval: LIST_REFETCH_INTERVAL
    });

    const tabs = useMemo(() => {
        if (user?.type === EUserType.INDIVIDUAL) {
            return allTabs.filter((item) => item.value !== 'sales');
        }
        return allTabs;
    }, [user]);

    const handleRefetch = useCallback(() => {
        if (activeTab === 'transactions') {
            return user?.type === EUserType.INDIVIDUAL ? refetchTransactions : refetchBusinessTransactions;
        } else if (activeTab === 'requests') {
            return refetchIntents;
        } else {
            return refetchSales;
        }
    }, [
        refetchIntents,
        refetchSales,
        refetchTransactions,
        refetchBusinessTransactions,
        activeTab,
        user
    ]);

    const isLoading = useMemo(() => {
        if (activeTab === 'transactions') {
            return user?.type === EUserType.INDIVIDUAL
                ? isTransactionsLoading
                : isBusinessTransactionsLoading;
        } else if (activeTab === 'requests') {
            return isIntentsLoading;
        } else {
            return isSalesLoading;
        }
    }, [
        isTransactionsLoading,
        isBusinessTransactionsLoading,
        isIntentsLoading,
        isSalesLoading,
        activeTab,
        user
    ]);

    const transactionsData = useMemo(() => {
        if (activeTab === 'transactions') {
            return user?.type === EUserType.INDIVIDUAL ? transactions?.data : businessTransactions?.data;
        } else if (activeTab === 'requests') {
            return intents?.data;
        } else {
            return sales?.data;
        }
    }, [transactions, businessTransactions, intents, sales, activeTab, user]);

    return (
        <div className={styles.wrapper}>
            <div className={styles.content}>
                <div className={styles.table_header}>
                    {/*<Typography variant="h4" className={styles.title}>
                        Transactions
                    </Typography>*/}
                    <Tabs onChange={(val) => setActiveTab(val)} tabs={tabs} activeTab={activeTab} />
                    <Button
                        className={styles.all}
                        rightIcon="arrow_right"
                        variant="text-gray"
                        onClick={() =>
                            navigate(activeTab === 'sales' ? routes.sales : routes.transactions)
                        }>
                        {submitLocale('view_all')}
                    </Button>
                </div>
                <TransactionTable
                    activeTab={activeTab}
                    refetchTransactions={handleRefetch}
                    isTransactionsLoading={isLoading}
                    isSale={activeTab === 'sales'}
                    transactions={transactionsData || []}
                />
            </div>
        </div>
    );
};

export default TransactionsSection;
